import { ITreeGroup, ITreeLeaf, ITreePluginsProps, ITreeStylingParams } from '../../tree.types'
import TreeLeafNode from '../TreeLeafNode'
import { memo } from 'react'
import cn from '../../../../utils/cn'
import css from '@/shared/Tree/tree.module.css'
import SmoothDropdown from '../../../Animated/SmoothDropdown/SmoothDropdown'
import TreeGroupNodeExpandControl from '@/shared/Tree/components/TreeGroupNode/TreeGroupNodeExpandControl'

interface IProps<Leaf extends Record<string, any>, Group extends Record<string, any>, GroupId extends string | number>
  extends ITreePluginsProps {
  group: ITreeGroup<Group, GroupId>

  groupsMap: Record<GroupId, ITreeGroup<Group, GroupId>[]>
  leafsMap: Record<GroupId, ITreeLeaf<Leaf, GroupId>[]>

  renderLeaf: (leaf: ITreeLeaf<Leaf, GroupId>) => JSX.Element | string
  renderGroup: (group: ITreeGroup<Group, GroupId>) => JSX.Element | string

  onClickLeaf?: (leaf: Leaf) => void
  onClickGroup?: (group: Group) => void

  styling?: ITreeStylingParams
  isRoot?: boolean
}

const TreeGroupNode = <
  Leaf extends Record<string, any>,
  Group extends Record<string, any>,
  GroupId extends string | number,
>(
  props: IProps<Leaf, Group, GroupId>
) => {
  const isOpen = props.expandCtrl ? props.expandCtrl.expanded.has(props.group.id) : true
  const normalizedLeafs = props.leafsMap[props.group.id] || []
  const normalizedGroups = props.groupsMap[props.group.id] || []
  const isHasChildren = normalizedLeafs.length || normalizedGroups.length

  const handleClickGroup = () => {
    if (props.selectCtrl) {
      props.selectCtrl.handleSelect(`${props.group.id}-group`)
    }
    if (isHasChildren && !isOpen) {
      props.expandCtrl.toggleItem(props.group.id)
    }
    if (props.onClickGroup) {
      props.onClickGroup(props.group.data)
    }
  }

  const handleVisibilityToggle = () => {
    props.expandCtrl.toggleItem(props.group.id)
  }

  const renderGroupChildren = () => {
    if (!isHasChildren) return null

    return (
      <SmoothDropdown className="overflow-inherit" overflow="visible" open={isOpen} style={{ paddingLeft: '16px' }}>
        {normalizedGroups.map((group) => (
          <TreeGroupNode {...props} isRoot={false} group={group as any} key={group.id} />
        ))}
        {Boolean(normalizedLeafs.length) && (
          <div className="tree_leafs-content">
            {normalizedLeafs.map((leaf) => (
              <TreeLeafNode
                onClickLeaf={props.onClickLeaf}
                leaf={leaf as any}
                leafRenderer={props.renderLeaf}
                key={leaf.key}
                selectCtrl={props.selectCtrl}
                styling={props.styling}
              />
            ))}
          </div>
        )}
      </SmoothDropdown>
    )
  }

  const groupContentClassName = cn('inline-flex-gap width-100', css.groupWrapper, props.styling?.groupClassname)
  const groupContentCustomStyles = props.styling?.groupStyles ?? {}

  return (
    <div className={props.isRoot ? undefined : 'tree_content'}>
      <div
        data-selected={props.selectCtrl && props.group.id === props.selectCtrl.selectedItem}
        className={groupContentClassName}
        onClick={handleClickGroup}
        style={{ cursor: props.expandCtrl || props.onClickGroup ? 'pointer' : 'default', ...groupContentCustomStyles }}
      >
        <TreeGroupNodeExpandControl
          expandable={Boolean(props.expandCtrl) && Boolean(isHasChildren)}
          groupType={props.group.type || 'folder'}
          open={isOpen}
          onToggle={handleVisibilityToggle}
        />

        {props.renderGroup(props.group)}
      </div>
      {renderGroupChildren()}
    </div>
  )
}

export default memo(TreeGroupNode)
